import { useState } from 'react';
import type { Lead, FormaPagamento } from '@/mocks/admin';
import CustomSelect from './CustomSelect';

interface EditarParticipanteModalProps {
  lead: Lead;
  precoPadrao: number;
  onClose: () => void;
  onSave: (updates: Partial<Lead>, novoPreco?: number) => void;
}

const formaOptions = [
  { value: 'pix', label: 'PIX' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'transferencia', label: 'Transferência' },
];

const parcelaOptions = [
  { value: '1', label: '1× (à vista)' },
  { value: '2', label: '2×' },
  { value: '3', label: '3×' },
  { value: '4', label: '4×' },
  { value: '5', label: '5×' },
  { value: '6', label: '6×' },
  { value: '8', label: '8×' },
  { value: '10', label: '10×' },
  { value: '12', label: '12×' },
];

// Formata número para exibição brasileira (1.234,56)
const formatNumber = (v: number) => {
  if (!v && v !== 0) return '';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parse string formatada brasileira para número
const parseNumber = (str: string) => {
  if (!str) return 0;
  const cleaned = str.replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const formatCurrency = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function EditarParticipanteModal({ lead, precoPadrao, onClose, onSave }: EditarParticipanteModalProps) {
  const [forma_pagamento, setFormaPagamento] = useState<FormaPagamento | ''>(lead.forma_pagamento ?? '');
  const [num_parcelas, setNumParcelas] = useState(lead.num_parcelas ?? 1);

  // Valor numérico real (usado nos cálculos)
  const [preco_programa, setPrecoPrograma] = useState(lead.preco_programa ?? precoPadrao);
  const [valor_subsidio, setValorSubsidio] = useState(lead.valor_subsidio ?? 0);
  const [valor_entrada, setValorEntrada] = useState(lead.valor_entrada ?? 0);

  // Textos formatados para os inputs (sem "0" na frente)
  const [precoText, setPrecoText] = useState(formatNumber(lead.preco_programa ?? precoPadrao));
  const [subsidioText, setSubsidioText] = useState(formatNumber(lead.valor_subsidio ?? 0));
  const [entradaText, setEntradaText] = useState(formatNumber(lead.valor_entrada ?? 0));

  const valorFinal = preco_programa - valor_subsidio;
  const valorParcelado = num_parcelas > 0 ? (valorFinal - valor_entrada) / num_parcelas : valorFinal;

  const handleSave = () => {
    onSave({
      forma_pagamento: forma_pagamento || undefined,
      num_parcelas,
      valor_subsidio: valor_subsidio || undefined,
      valor_entrada: valor_entrada || undefined,
    }, preco_programa);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[99999] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-100 flex-shrink-0">
                <i className="ri-user-settings-line text-emerald-600 text-lg" />
              </div>
              <div>
                <h2 className="text-gray-900 font-black text-base">Editar Participante</h2>
                <p className="text-gray-400 text-xs">{lead.nome}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-400 text-lg" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Forma de pagamento */}
          <div>
            <label className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-2 block">Forma de Pagamento</label>
            <CustomSelect
              options={formaOptions}
              value={forma_pagamento}
              onChange={(v) => setFormaPagamento(v as FormaPagamento)}
              placeholder="Selecione..."
            />
          </div>

          {/* Parcelas */}
          <div>
            <label className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-2 block">Número de Parcelas</label>
            <CustomSelect
              options={parcelaOptions}
              value={String(num_parcelas)}
              onChange={(v) => setNumParcelas(Number(v))}
              placeholder="Parcelas"
            />
            {num_parcelas > 1 && (
              <p className="text-emerald-600 text-xs font-semibold mt-1.5">
                Valor da parcela: {formatCurrency(valorParcelado)}
              </p>
            )}
          </div>

          {/* Valor do Programa */}
          <div>
            <label className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-2 block">Valor do Programa</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">R$</span>
              <input
                type="text"
                inputMode="decimal"
                value={precoText}
                onChange={(e) => {
                  setPrecoText(e.target.value);
                  setPrecoPrograma(parseNumber(e.target.value));
                }}
                placeholder="0,00"
                className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-900 font-semibold text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
              />
            </div>
          </div>

          {/* Subsídio SEBRAE */}
          <div>
            <label className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-2 block">Subsídio SEBRAE</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">R$</span>
              <input
                type="text"
                inputMode="decimal"
                value={subsidioText}
                onChange={(e) => {
                  setSubsidioText(e.target.value);
                  setValorSubsidio(parseNumber(e.target.value));
                }}
                placeholder="0,00"
                className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-900 font-semibold text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
              />
            </div>
            <p className="text-gray-400 text-xs mt-1">Valor descontado do programa para este participante</p>
          </div>

          {/* Valor de entrada */}
          <div>
            <label className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-2 block">Valor de Entrada</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">R$</span>
              <input
                type="text"
                inputMode="decimal"
                value={entradaText}
                onChange={(e) => {
                  setEntradaText(e.target.value);
                  setValorEntrada(parseNumber(e.target.value));
                }}
                placeholder="0,00"
                className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-900 font-semibold text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
              />
            </div>
            {valor_entrada > 0 && valorParcelado > 0 && (
              <p className="text-sky-600 text-xs font-semibold mt-1.5">
                Entrada · {num_parcelas}x de {formatCurrency(valorParcelado)}
              </p>
            )}
          </div>

          {/* Resumo */}
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-2">
            <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Resumo</p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Valor do programa</span>
              <span className="text-gray-700 font-semibold">{formatCurrency(preco_programa)}</span>
            </div>
            {valor_subsidio > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subsídio SEBRAE</span>
                <span className="text-emerald-600 font-semibold">− {formatCurrency(valor_subsidio)}</span>
              </div>
            )}
            {valor_entrada > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Entrada</span>
                <span className="text-sky-600 font-semibold">− {formatCurrency(valor_entrada)}</span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-2 flex justify-between">
              <span className="text-gray-700 font-bold">Valor final</span>
              <span className="text-gray-900 font-black text-lg">{formatCurrency(Math.max(0, valorFinal - valor_entrada))}</span>
            </div>
            {num_parcelas > 1 && valorParcelado > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400 text-xs">{num_parcelas}x sem juros</span>
                <span className="text-gray-500 text-xs font-semibold">{formatCurrency(valorParcelado)}/cada</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold text-sm py-3 rounded-xl transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm py-3 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-check-line text-sm" />
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
